// PATH: src/components/modules/devtools/components/ReleaseSyncFileHistoryDialog.js

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import RichDiffViewer from "./RichDiffViewer";
import { fetchReleaseSyncFileDetail } from "../utils/releaseSyncApi";

const shortHash = (value) => (value ? `${String(value).slice(0, 12)}…` : "—");

const formatDate = (value) => {
  if (!value) return "Unknown time";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const labelValue = (label, value, mono = false) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontFamily: mono ? "monospace" : "inherit",
        overflowWrap: "anywhere",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const EvidenceSummary = ({ asset, evidencePolicy }) => {
  const evidence = asset?.evidence || {};
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip size="small" label={`Confidence: ${asset?.confidence || "UNKNOWN"}`} />
          <Chip
            size="small"
            label={`Provenance: ${asset?.provenance_status || "UNKNOWN"}`}
          />
          <Chip
            size="small"
            label={`Target history: ${asset?.target_content_history_status || "UNKNOWN"}`}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Applied source saves: {evidence.source_applied_save_count || 0}; trusted content
          boundaries: {evidence.source_content_boundary_count || 0}. Applied target saves: {" "}
          {evidence.target_applied_save_count || 0}; trusted content boundaries: {" "}
          {evidence.target_content_boundary_count || 0}.
        </Typography>
        {evidencePolicy && (
          <Typography variant="caption" color="text.secondary">
            Evidence rule: an applied save row is required. Preview/change-log evidence alone is
            not treated as proof of an applied version, and an exact historical hash match is not
            deployment proof.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

const HistoryEvent = ({ event, side }) => {
  const change = event?.change || {};
  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <Chip size="small" label={side} />
          <Typography variant="body2" fontWeight={700}>
            {formatDate(event?.saved_at)}
          </Typography>
          <Chip size="small" variant="outlined" label={event?.evidence_status || "APPLIED_SAVE"} />
        </Stack>
        <Typography variant="body2">
          Change {change.change_id ? `#${change.change_id}` : "unmapped"}
          {change.title ? ` — ${change.title}` : ""}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {labelValue("Before SHA-256", shortHash(event?.before_sha256), true)}
          {labelValue("After SHA-256", shortHash(event?.after_sha256), true)}
        </Stack>
        {event?.save_summary && (
          <Typography variant="caption" color="text.secondary">
            {event.save_summary}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

const ChangeChain = ({ changes = [] }) => {
  if (!changes.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No subsequent source Change chain could be established from the applied-save evidence.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {changes.map((change) => (
        <Paper key={`change-${change.change_id}`} variant="outlined" sx={{ p: 1.25 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                Change #{change.change_id} {change.title ? `— ${change.title}` : ""}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {change.status || "Unknown status"}
                {change.module ? ` • ${change.module}` : ""}
                {change.developer ? ` • ${change.developer}` : ""}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 180 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Full Change
              </Typography>
              <Typography variant="body2">
                {change.full_change?.full_change_id
                  ? `#${change.full_change.full_change_id} ${change.full_change.title || ""}`
                  : "Not mapped"}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

const FullChangeGroups = ({ groups = [] }) => {
  if (!groups.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No Full Change grouping is available for the subsequent source Change chain.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {groups.map((group, index) => {
        const fullChange = group.full_change || null;
        return (
          <Paper
            key={`full-change-${group.full_change_id || `unmapped-${index}`}`}
            variant="outlined"
            sx={{ p: 1.25 }}
          >
            <Typography variant="body2" fontWeight={700}>
              {fullChange
                ? `Full Change #${fullChange.full_change_id} — ${fullChange.title || "Untitled"}`
                : "Unmapped Changes"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(group.changes || []).map((change) => `#${change.change_id}`).join(", ") || "—"}
            </Typography>
          </Paper>
        );
      })}
    </Stack>
  );
};

const ReleaseSyncFileHistoryDialog = ({
  open,
  onClose,
  asset,
  sourceProjectId,
  targetProjectId,
  direction,
  manualComparison = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let active = true;
    if (!open || !asset?.path || !sourceProjectId || !targetProjectId) {
      setDetail(null);
      setError("");
      return () => {
        active = false;
      };
    }

    const load = async () => {
      setLoading(true);
      setError("");
      setDetail(null);
      try {
        const result = await fetchReleaseSyncFileDetail({
          sourceProjectId,
          targetProjectId,
          direction,
          path: asset.path,
          manualComparison,
        });
        if (active) setDetail(result);
      } catch (err) {
        if (active) setError(err.message || "Failed to load file reconciliation detail.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [open, asset?.path, sourceProjectId, targetProjectId, direction, manualComparison]);

  const scanAsset = detail?.scanAsset || asset || null;
  const historyAsset = detail?.historyAsset || asset || null;
  const currentDiff = scanAsset?.text_diff || null;

  const timeline = useMemo(() => {
    const source = (historyAsset?.source_history || []).map((event) => ({ ...event, side: "SOURCE" }));
    const target = (historyAsset?.target_history || []).map((event) => ({ ...event, side: "TARGET" }));
    return [...source, ...target].sort((left, right) => {
      const leftTime = new Date(left.saved_at || 0).getTime();
      const rightTime = new Date(right.saved_at || 0).getTime();
      if (leftTime !== rightTime) return leftTime - rightTime;
      return String(left.side).localeCompare(String(right.side));
    });
  }, [historyAsset]);

  const inferred = historyAsset?.target_inferred_source_position || null;
  const isTargetOnly = scanAsset?.state === "TARGET_ONLY";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 6 }}>
        Release Sync asset detail
        {asset?.path ? (
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {asset.path}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Loading current diff and file history on demand…</Typography>
          </Stack>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && scanAsset && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>Current source / target state</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
                <Chip size="small" label={scanAsset.state || "UNKNOWN"} />
                <Chip size="small" variant="outlined" label={scanAsset.asset_type || "OTHER"} />
                <Chip size="small" variant="outlined" label={scanAsset.content_relation || "UNKNOWN"} />
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 1.5,
                }}
              >
                {labelValue("Source SHA-256", scanAsset.source_sha256 || "Missing", true)}
                {labelValue("Target SHA-256", scanAsset.target_sha256 || "Missing", true)}
                {labelValue("Source kind", scanAsset.source_kind)}
                {labelValue("Target kind", scanAsset.target_kind)}
              </Box>
            </Paper>

            {isTargetOnly && (
              <Alert severity="warning">
                This is target-only/drift evidence. It is not an automatic deletion candidate. A
                future delete decision must require an explicit source delete event/evidence and a
                separate write-capable release phase.
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>Current text diff</Typography>
              {currentDiff?.status === "available" ? (
                <>
                  {currentDiff.truncated && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      Diff is truncated to the backend read-only preview limit.
                    </Alert>
                  )}
                  <RichDiffViewer
                    diffText={currentDiff.diff}
                    emptyMessage="The current source and target text are identical."
                    maxHeight={420}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {currentDiff?.status
                    ? `Text diff is ${String(currentDiff.status).toLowerCase().replace(/_/g, " ")}.`
                    : "No text diff is available for this asset state."}
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Historical position and confidence</Typography>
              <EvidenceSummary asset={historyAsset} evidencePolicy={detail?.evidencePolicy} />
            </Box>

            {inferred ? (
              <Alert severity="info">
                <Typography variant="body2" fontWeight={700}>
                  Target inferred historical source position
                </Typography>
                <Typography variant="body2">
                  {inferred.boundary === "BEFORE_APPLIED_SAVE" ? "Before" : "After"} applied save for
                  Change #{inferred.change?.change_id || "?"} at {formatDate(inferred.saved_at)}.
                  Exact matches: {inferred.exact_match_count || 1}
                  {inferred.ambiguous_exact_match ? " (multiple distinct historical positions)." : "."}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  This is an inferred historical source content position only. It does not prove
                  that this Change was deployed to the target environment.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="warning">
                No exact current-target hash match was found in trusted applied source content
                boundaries. Historical provenance remains partial or unknown.
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>Subsequent source Changes</Typography>
              <ChangeChain changes={historyAsset?.subsequent_source_changes || []} />
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Full Change grouping</Typography>
              <FullChangeGroups groups={historyAsset?.subsequent_full_change_groups || []} />
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Applied-save timeline</Typography>
              {timeline.length ? (
                <Stack spacing={1}>
                  {timeline.map((event, index) => (
                    <HistoryEvent
                      key={`${event.side}-${event.change_file_id || index}-${event.event_index || index}`}
                      event={event}
                      side={event.side}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No project-scoped applied-save history is available for this file.
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseSyncFileHistoryDialog;
